/**
 * As chaves de acesso (WebAuthn) — a versão em TypeScript do
 * `web/webauthn_bridge.js` que o Flutter carrega no `index.html`.
 *
 * Aqui não se decide nada: o servidor emite o desafio e confere a resposta.
 * Isto só leva um e traz o outro. Nenhum material de chave passa por este
 * arquivo — o que sai daqui é a mesma coisa que o navegador entregou.
 */

/** `ArrayBuffer` -> base64url, que é como o servidor fala. */
function paraB64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let bruto = '';
  for (const b of bytes) bruto += String.fromCharCode(b);
  return btoa(bruto).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** base64url -> bytes. O `+`/`/` e o enchimento voltam antes do `atob`. */
function deB64url(valor: string): Uint8Array {
  const normal = valor.replace(/-/g, '+').replace(/_/g, '/');
  const cheio = normal + '='.repeat((4 - (normal.length % 4)) % 4);
  const bruto = atob(cheio);
  const bytes = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i += 1) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}

/** O navegador sabe fazer chaves de acesso? */
export function temSuporte(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function' &&
    Boolean(navigator.credentials && navigator.credentials.create)
  );
}

/**
 * Tem leitor de digital, Face ID ou Windows Hello aqui dentro?
 *
 * Uma chave de segurança USB funcionaria mesmo sem isso, então esta resposta
 * só decide como o botão é escrito.
 */
export async function temAutenticadorDoAparelho(): Promise<boolean> {
  if (!temSuporte()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** O navegador oferece a chave dentro do próprio campo de senha? */
export async function temPreenchimentoAutomatico(): Promise<boolean> {
  if (!temSuporte()) return false;
  try {
    const pc = window.PublicKeyCredential as unknown as {
      isConditionalMediationAvailable?: () => Promise<boolean>;
    };
    return typeof pc.isConditionalMediationAvailable === 'function'
      ? await pc.isConditionalMediationAvailable()
      : false;
  } catch {
    return false;
  }
}

/** O que o servidor manda: tudo em base64url, que vira bytes antes de usar. */
type OpcoesCruas = Record<string, unknown>;

interface Descritor {
  id: string;
  [k: string]: unknown;
}

/** Troca por bytes o que o WebAuthn exige em bytes, e deixa o resto igual. */
function decodificar(opcoes: OpcoesCruas): OpcoesCruas {
  const saida: OpcoesCruas = { ...opcoes };

  if (typeof saida.challenge === 'string') saida.challenge = deB64url(saida.challenge);

  const usuario = saida.user as { id?: unknown } | undefined;
  if (usuario && typeof usuario.id === 'string') {
    saida.user = { ...usuario, id: deB64url(usuario.id) };
  }

  for (const campo of ['excludeCredentials', 'allowCredentials'] as const) {
    const lista = saida[campo];
    if (!Array.isArray(lista)) continue;
    saida[campo] = (lista as Descritor[]).map((c) =>
      typeof c.id === 'string' ? { ...c, id: deB64url(c.id) } : c,
    );
  }

  return saida;
}

/** A credencial já em base64url, do jeito que o servidor confere. */
export type CredencialDeChave = Record<string, unknown>;

export interface Cerimonia {
  ok: boolean;
  /** Desistir não é erro: quem fechou o pedido não precisa de desculpa. */
  cancelada?: boolean;
  recado?: string;
  credencial?: CredencialDeChave;
}

/**
 * O erro do navegador virado em recado.
 *
 * São os mesmos quatro casos do `describe` do bridge: cancelar não vira erro,
 * chave repetida e conexão insegura têm explicação própria, e o resto cai numa
 * frase que serve para qualquer coisa.
 *
 * Os recados ficam em português AQUI e passam pelo `traduzir()` só na hora de
 * aparecer, como o `nome` dos `DESTINOS`. Quem inventar um quinto recado tem
 * de pôr o verbete em `palavras.ts` junto: nenhum dos dois vigias enxerga
 * isto, porque a frase não está dentro de um `traduzir()` nem dentro de JSX --
 * e o `traduzir()` devolve a própria frase quando não acha, então o esquecido
 * sai em português numa tela em inglês, sem reclamar de nada.
 */
function descrever(erro: unknown): Cerimonia {
  const nome = erro instanceof Error ? erro.name : '';
  if (nome === 'NotAllowedError' || nome === 'AbortError') return { ok: false, cancelada: true };
  if (nome === 'InvalidStateError') {
    return { ok: false, recado: 'Esse dispositivo já tem uma chave salva aqui.' };
  }
  if (nome === 'SecurityError') {
    return { ok: false, recado: 'Chaves só funcionam em uma conexão segura.' };
  }
  return { ok: false, recado: 'Não foi possível usar sua chave neste dispositivo.' };
}

const SEM_SUPORTE: Cerimonia = {
  ok: false,
  recado: 'Este navegador não suporta chaves de acesso.',
};

/** Cria uma chave neste aparelho. */
export async function criar(opcoes: OpcoesCruas): Promise<Cerimonia> {
  if (!temSuporte()) return SEM_SUPORTE;
  try {
    const credencial = (await navigator.credentials.create({
      publicKey: decodificar(opcoes) as unknown as PublicKeyCredentialCreationOptions,
    })) as PublicKeyCredential | null;
    if (!credencial) return { ok: false, cancelada: true };

    const resposta = credencial.response as AuthenticatorAttestationResponse;
    return {
      ok: true,
      credencial: {
        id: credencial.id,
        rawId: paraB64url(credencial.rawId),
        type: credencial.type,
        response: {
          clientDataJSON: paraB64url(resposta.clientDataJSON),
          attestationObject: paraB64url(resposta.attestationObject),
        },
      },
    };
  } catch (erro) {
    return descrever(erro);
  }
}

/** Usa uma chave já criada. `condicional` oferece a chave dentro do campo. */
export async function usar(opcoes: OpcoesCruas, condicional = false): Promise<Cerimonia> {
  if (!temSuporte()) return SEM_SUPORTE;
  try {
    const pedido: CredentialRequestOptions = {
      publicKey: decodificar(opcoes) as unknown as PublicKeyCredentialRequestOptions,
    };
    if (condicional) pedido.mediation = 'conditional';

    const credencial = (await navigator.credentials.get(pedido)) as PublicKeyCredential | null;
    if (!credencial) return { ok: false, cancelada: true };

    const resposta = credencial.response as AuthenticatorAssertionResponse;
    return {
      ok: true,
      credencial: {
        id: credencial.id,
        rawId: paraB64url(credencial.rawId),
        type: credencial.type,
        response: {
          clientDataJSON: paraB64url(resposta.clientDataJSON),
          authenticatorData: paraB64url(resposta.authenticatorData),
          signature: paraB64url(resposta.signature),
          userHandle: resposta.userHandle ? paraB64url(resposta.userHandle) : null,
        },
      },
    };
  } catch (erro) {
    return descrever(erro);
  }
}
